import { ScrollArea } from '@mantine/core';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { PivotAggregateFormatEnum, PivotModel } from 'api-hooks/common/model';
import { money2number, string2money } from 'common/utils/string';
import PaginationLoader from 'components/common/pagination-loader';
import { IColumn } from 'components/elements/table-component/table-component';
import usePaginateData from 'hooks/use-paginate-data';
import { camelize, camelizeKeys } from 'humps';
import * as React from 'react';

import { usePivotTable } from './hook';
import navigateByAcl from './navigate-by-acl';
import { LinkText, Table, Tbody, Td, Th, Thead } from './style';
import useGeneratePivotTableBody from './use-generate-pivot-table-body';
import useGeneeratePivotTableHeader from './use-generate-pivot-table-header';

type PivotData = {
  pivot: PivotModel;
  data: any;
};

interface PivotTableProps<T extends PivotData> {
  queryResultData?: T;
  numberColoredFields?: string[];
}

function CellComponent({ format, value }) {
  switch (format) {
    case PivotAggregateFormatEnum.money:
      return value !== null ? string2money(String(value)) : '';
    case PivotAggregateFormatEnum.percentage:
      return money2number(value) + '%';
    default:
      return String(value || '-');
  }
}

export default function PivotTable<T extends PivotData>(
  props: PivotTableProps<T>,
) {
  const result = props.queryResultData;
  const { tableId } = usePivotTable();

  const columns = React.useMemo(() => {
    if (!result?.pivot) {
      return [];
    }
    const cols: IColumn<any>[] = (result?.pivot?.value?.rows || []).map(
      (row) => ({
        header: row.label,
        accessorKey: camelize(row.name),
      }),
    );

    return cols.concat(
      (result?.pivot?.value?.aggregates || []).map((aggr, index) => {
        return {
          header: aggr.label,
          accessorKey: camelize(aggr.name),
          link: aggr.link && camelize(aggr.link),
          valueFormat: aggr.format,
          cell: ({ getValue }) =>
            CellComponent({ format: aggr.format, value: getValue() as any }),
        };
      }),
    );
  }, [result?.pivot]);

  const [data, keyMap, linkMap] = React.useMemo(() => {
    if (!result) {
      return [[], [], []];
    }

    const keyMap: string[][] = [];
    const linkMap: any[][] = [];

    const data = (result.data || []).map((datum, parentIdx) => {
      const row = camelizeKeys(datum);
      // if is aggreagate row, set accessor as aggregate row's label
      if (Number.isInteger(row.position)) {
        //@ts-ignore
        row[String(columns[row.position].accessorKey)] = row.label;
        //@ts-ignore
        row[String(columns[row.position].accessorKey) + 'Key'] = row.label;
      }

      let _lastKey = '';
      const linkKeys: any = [];

      keyMap.push(
        (result?.pivot?.value?.rows || []).map((pivotRow, index) => {
          _lastKey += row[camelize(pivotRow.name) + 'Key'];

          linkKeys.push(pivotRow.link ? row[camelize(pivotRow.link)] : null);

          return _lastKey;
        }),
      );

      linkMap.push(linkKeys);

      return row;
    });

    return [data, keyMap, linkMap];
  }, [result, columns]);

  const table = useReactTable({
    data,
    columns,
    getSubRows: (row) => row.subRows,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getRowModel, getHeaderGroups } = table;
  const rows = getRowModel().rows;
  const headerGroups = getHeaderGroups();

  const generateTableBodyData = useGeneratePivotTableBody();
  const generateTableHeaderData = useGeneeratePivotTableHeader();
  const tableHeader = generateTableHeaderData(headerGroups);

  const {
    items: tableBody,
    hasNextPage,
    loadNextPage,
  } = usePaginateData(
    generateTableBodyData(rows, keyMap, linkMap, result, props),
    10,
  );

  const noHeader = !tableHeader[0][1]?.content;

  return (
    <ScrollArea type="always">
      <Table id={tableId}>
        <Thead>
          {tableHeader.map((headerGroup) => {
            const headerId = headerGroup.find(
              (header) => header?.headerId,
            )?.headerId;
            return (
              <tr key={headerId}>
                {headerGroup.map((header, columnIdx) => {
                  return (
                    <Th
                      key={header.headerId}
                      colSpan={header.colSpan}
                      rowSpan={header.rowSpan}
                    >
                      {header.content}
                    </Th>
                  );
                })}
              </tr>
            );
          })}
        </Thead>
        <Tbody>
          {tableBody.map((row, rowIdx) => {
            const isAggregate = row.tdClassName !== undefined;
            return (
              <tr
                key={row.rowId}
                style={{
                  position: isAggregate ? 'sticky' : 'static',
                  top: isAggregate ? 1 * (noHeader ? 0 : 36) : undefined,
                }}
              >
                {row.cells.map((cell, cellIdx) => {
                  if (cell === null) return null;

                  const {
                    alignment,
                    cellId,
                    colSpan,
                    color,
                    content,
                    linkText,
                    rowSpan,
                    tdClassName,
                  } = cell;

                  return (
                    <Td
                      key={cellId}
                      className={[tdClassName, 'tableexport-string'].join(' ')}
                      colSpan={colSpan}
                      rowSpan={rowSpan}
                      $style={{
                        color,
                        minWidth: linkText ? '80px' : '150px',
                      }}
                      align={alignment}
                    >
                      {linkText ? (
                        <LinkText
                          href={{
                            pathname: navigateByAcl[linkText.acl],
                            query: { id: linkText?.id },
                          }}
                          style={{
                            textAlign: 'center',
                            display: 'block',
                          }}
                        >
                          {linkText.cell}
                        </LinkText>
                      ) : (
                        content
                      )}
                    </Td>
                  );
                })}
              </tr>
            );
          })}
        </Tbody>
      </Table>
      <PaginationLoader hasNextPage={hasNextPage} loadNextPage={loadNextPage} />
    </ScrollArea>
  );
}
